var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Drives the glass damage modal stepper and aligns step gating with validation.
 * touches: insurer load, behavior
 * invariants: enabled sections must match insurer configuration and validation constraints.
 * inputs: glass damage change state, validation context, self care settings.
 * outputs: navigates steps and triggers validation or save actions.
 * see: frontend/src/shared/validator/constraints/glassDamageConstraints.ts, frontend/src/declaration/helpers/glassDamageModalConfig.ts
 */
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton } from '@claims-ia/design-system';
import { Watch } from 'vue-property-decorator';
import Modal from '@declaration/components/UI/Modal.vue';
import RadioPrimaryButton from '@declaration/components/UI/RadioPrimaryButton.vue';
import DamageType from '@declaration/components/glassDamage/DamageDescription/DamageType.vue';
import PanesDescription from '@declaration/components/glassDamage/DamageDescription/PanesDescription.vue';
import GlazingType from '@declaration/components/glassDamage/DamageDescription/GlazingType.vue';
import GlassType from '@declaration/components/glassDamage/DamageDescription/GlassType.vue';
import PicturesCollection from '@declaration/components/glassDamage/DamageDescription/PicturesCollection.vue';
import { GLASS_DAMAGE_TYPE_TRAD_KEYS } from '@shared/constants/glassDamageConstants';
import { GLASS_DAMAGE_TYPE_IMAGES, GLASS_DAMAGE_TYPE_IMAGE_CHOICES, GLASS_DAMAGE_TYPE_PLAIN_CHOICES, } from '@declaration/constants/glassDamageConstants';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
import { getEnabledSectionsForDamageType, arePhotosRequired, } from '@declaration/helpers/glassDamageModalConfig';
import { GlassDamageModalSection } from '@shared/insurer';
const glassDamage = namespace('glassDamage');
const settings = namespace('settings');
var Sections;
(function (Sections) {
    Sections["TypeSelection"] = "TypeSelection";
    Sections["DamageDescription"] = "DamageDescription";
    Sections["GlazingType"] = "GlazingType";
    Sections["GlassType"] = "GlassType";
    Sections["Pictures"] = "Pictures";
})(Sections || (Sections = {}));
let GlassDamageModal = class GlassDamageModal extends Vue {
    constructor() {
        super(...arguments);
        this.isSaving = false;
        this.SECTIONS = Sections;
        this.photosValid = false;
        this.validateRequested = false;
        this.selectedSection = null;
    }
    onSectionChanged() {
        var _a;
        // Valider pour remplir field.errors (nécessaire pour fieldIsValid)
        this.validateGlassDamage(this.changeKey);
        // Masquer les erreurs visuelles de TOUS les champs
        Object.keys(((_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.fields) || {}).forEach((fieldName) => {
            this.$store.commit('glassDamage/UNTOUCH_FIELD', { changeKey: this.changeKey, fieldName });
        });
    }
    get selfCareSettings() {
        return this.setting('selfCareSettings');
    }
    get title() {
        return this.glassDamageType && this.section !== Sections.TypeSelection
            ? this.$tc(GLASS_DAMAGE_TYPE_TRAD_KEYS[this.glassDamageType])
            : this.$tc('claim.damage.glass_damage.form.title');
    }
    get image() {
        if (this.section !== Sections.TypeSelection) {
            const type = this.glassDamageType;
            if (type && type in GLASS_DAMAGE_TYPE_IMAGES)
                return GLASS_DAMAGE_TYPE_IMAGES[type];
        }
        return null;
    }
    get buttonText() {
        const isLastSection = this.getNextSection(this.section) === null;
        return isLastSection
            ? this.$tc('claim.damage.glass_damage.form.validate')
            : this.$tc('claim.damage.glass_damage.form.next');
    }
    get section() {
        if (!this.selectedSection) {
            if (this.glassDamage.type && this.validation.isValid) {
                const firstSection = this.getNextSection(Sections.TypeSelection);
                return firstSection || Sections.TypeSelection;
            }
            return Sections.TypeSelection;
        }
        return this.selectedSection;
    }
    get hasDisableDamagesPictures() {
        return insurerHasFeature(InsurerFeatureFlag.DisableDamagesPictures);
    }
    get enabledSections() {
        return getEnabledSectionsForDamageType(this.glassDamageType, this.selfCareSettings);
    }
    isSectionEnabled(section) {
        return this.enabledSections.includes(section);
    }
    getNextSection(currentSection) {
        const sectionOrder = [
            { internal: Sections.DamageDescription, config: GlassDamageModalSection.DamageDescription },
            { internal: Sections.GlazingType, config: GlassDamageModalSection.GlazingType },
            { internal: Sections.GlassType, config: GlassDamageModalSection.GlassType },
            { internal: Sections.Pictures, config: GlassDamageModalSection.Pictures },
        ];
        // Special case: TypeSelection should return the first enabled section
        if (currentSection === Sections.TypeSelection) {
            for (const { internal, config } of sectionOrder) {
                if (this.isSectionEnabled(config)) {
                    if (internal === Sections.Pictures && this.hasDisableDamagesPictures) {
                        continue;
                    }
                    return internal;
                }
            }
            return null;
        }
        // For other sections, find the next enabled section after the current one
        let foundCurrent = false;
        for (const { internal, config } of sectionOrder) {
            if (foundCurrent && this.isSectionEnabled(config)) {
                if (internal === Sections.Pictures && this.hasDisableDamagesPictures) {
                    continue;
                }
                return internal;
            }
            if (internal === currentSection) {
                foundCurrent = true;
            }
        }
        return null;
    }
    get isValidStep() {
        return this.isValidStepCheck(this.section);
    }
    isValidStepCheck(step) {
        var _a;
        switch (step) {
            case Sections.TypeSelection:
                return this.validation.fieldIsValid('type');
            case Sections.DamageDescription:
                // Only validate if this section is enabled
                if (!this.isSectionEnabled(GlassDamageModalSection.DamageDescription)) {
                    return true;
                }
                return (this.validation.fieldIsValid('numberOfPanes') &&
                    this.validation.fieldIsValid('widestPaneWidth') &&
                    this.validation.fieldIsValid('widestPaneHeight'));
            case Sections.GlazingType:
                // Only validate if this section is enabled
                if (!this.isSectionEnabled(GlassDamageModalSection.GlazingType)) {
                    return true;
                }
                return this.validation.fieldIsValid('glazingType');
            case Sections.GlassType:
                // Only validate if this section is enabled
                if (!this.isSectionEnabled(GlassDamageModalSection.GlassType)) {
                    return true;
                }
                return this.validation.fieldIsValid('glassType');
            case Sections.Pictures: {
                // Photos are optional by default, required only if explicitly configured
                const photosRequired = arePhotosRequired((_a = this.glassDamage) === null || _a === void 0 ? void 0 : _a.type, this.selfCareSettings);
                if (photosRequired) {
                    return this.photosValid;
                }
                return true;
            }
            default:
                return false;
        }
    }
    nextStep() {
        this.updateGlassDamage({ changeKey: this.changeKey, data: { type: this.glassDamage.type } });
        this.validateGlassDamage(this.changeKey);
        if (this.isValidStep) {
            if (this.section === Sections.TypeSelection) {
                const firstEnabledSection = this.getNextSection(Sections.TypeSelection);
                if (firstEnabledSection) {
                    this.selectedSection = firstEnabledSection;
                }
                else {
                    this.save();
                }
            }
            else {
                const nextSection = this.getNextSection(this.section);
                if (nextSection) {
                    this.selectedSection = nextSection;
                }
                else {
                    this.save();
                }
            }
        }
    }
    get change() {
        return this.changeGetter(this.changeKey);
    }
    get validation() {
        return this.validationGetter(this.changeKey);
    }
    get glassDamage() {
        return this.change.data;
    }
    get glassDamageType() {
        return this.glassDamage.type;
    }
    get glassDamageTypeImageChoices() {
        return GLASS_DAMAGE_TYPE_IMAGE_CHOICES;
    }
    get glassDamageTypeButtonChoices() {
        return GLASS_DAMAGE_TYPE_PLAIN_CHOICES;
    }
    revealValidation() {
        var _a;
        this.$store.dispatch('error/add', this.$tc('error.form.please_validate_message'));
        this.validateGlassDamage(this.changeKey);
        // Ne garder que les erreurs de la section courante
        const sectionFields = {
            [Sections.TypeSelection]: ['type'],
            [Sections.DamageDescription]: ['numberOfPanes', 'widestPaneWidth', 'widestPaneHeight'],
            [Sections.GlazingType]: ['glazingType'],
            [Sections.GlassType]: ['glassType'],
            [Sections.Pictures]: [],
        };
        const currentFields = sectionFields[this.section] || [];
        Object.keys(((_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.fields) || {}).forEach((fieldName) => {
            if (!currentFields.includes(fieldName)) {
                this.$store.commit('glassDamage/UNTOUCH_FIELD', { changeKey: this.changeKey, fieldName });
            }
        });
        this.validateRequested = true;
    }
    async save() {
        this.isSaving = true;
        try {
            await this.saveChanges();
            this.closeModal();
        }
        catch (err) {
            this.$store.dispatch('error/add', 'Une erreur est survenue, merci de réessayer.');
        }
        this.isSaving = false;
    }
    closeModal() {
        this.selectedSection = null;
        this.validateRequested = false;
        this.$emit('close');
    }
    onClose() {
        this.closeModal();
    }
    onPhotosValidUpdate(val) {
        this.photosValid = val;
    }
};
__decorate([
    glassDamage.Getter('change')
], GlassDamageModal.prototype, "changeGetter", void 0);
__decorate([
    glassDamage.Action('updateDamage')
], GlassDamageModal.prototype, "updateGlassDamage", void 0);
__decorate([
    glassDamage.Action('resetState')
], GlassDamageModal.prototype, "resetState", void 0);
__decorate([
    glassDamage.Getter('validation')
], GlassDamageModal.prototype, "validationGetter", void 0);
__decorate([
    glassDamage.Action('validateAndRevealDamages')
], GlassDamageModal.prototype, "validateGlassDamage", void 0);
__decorate([
    glassDamage.Action('saveChanges')
], GlassDamageModal.prototype, "saveChanges", void 0);
__decorate([
    settings.Getter('setting')
], GlassDamageModal.prototype, "setting", void 0);
__decorate([
    Watch('section')
], GlassDamageModal.prototype, "onSectionChanged", null);
GlassDamageModal = __decorate([
    Component({
        components: {
            Modal,
            DamageType,
            PanesDescription,
            GlazingType,
            GlassType,
            PicturesCollection,
            RadioPrimaryButton,
            UiButton,
        },
        props: {
            isDisplayed: { type: Boolean, required: true },
            changeKey: { type: [Number, String], default: null },
        },
    })
], GlassDamageModal);
export default GlassDamageModal;
